import { useEffect, useState, useCallback } from "react";
import {
  Modal,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Text,
  Progress,
  LoadingOverlay,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { toast } from "react-toastify";
import { getStrength, passwordRegex } from "../utils/regex";
import authService from "../services/authService";

export interface ChangePasswordModalProps {
  visible: boolean;
  onClose: () => void;
}

const ChangePasswordModal = ({
  visible,
  onClose,
}: ChangePasswordModalProps) => {
  const form = useForm({
    initialValues: {
      email: "",
      currentPwd: "",
      newPwd: "",
      confirmPwd: "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : "Geçerli bir e-posta girin",
      newPwd: (value) =>
        passwordRegex.test(value)
          ? null
          : "Şifre en az 8 karakter, büyük harf, küçük harf ve rakam içermelidir.",
      confirmPwd: (value, values) =>
        value !== values.newPwd ? "Şifreler eşleşmiyor" : null,
    },
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
  }, [visible, form]);

  const handleSubmit = useCallback(
    async (values: typeof form.values) => {
      if (form.validate().hasErrors) {
        toast.error("Lütfen gerekli alanları doldurunuz.", {
          toastId: "change-password-error",
        });
        return;
      }
      setLoading(true);
      const _values = {
        email: values.email,
        password: values.currentPwd,
        newPassword: values.newPwd,
      };
      await authService
        .resetPassword(_values)
        .then(() => {
          toast.success("Şifreniz değiştirildi", {
            toastId: "change-password",
          });
        })
        .catch((err) => {
          toast.error("Şifreniz değiştirilemedi", {
            toastId: "change-password-error",
          });
        })
        .finally(() => {
          setLoading(false);
          onClose();
        });
    },
    [form, onClose]
  );

  return (
    <Modal
      opened={visible}
      onClose={onClose}
      title={<Text size="xl">Şifre Değiştir</Text>}
      size="md"
      centered
      zIndex={100}
    >
      <LoadingOverlay
        visible={loading}
        opacity={0}
        zIndex={100}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="E-posta"
          placeholder="E-posta adresiniz"
          required
          type="email"
          size="md"
          className="w-full"
          maxLength={60}
          {...form.getInputProps("email")}
        />

        <PasswordInput
          label="Mevcut Şifre"
          placeholder="Mevcut şifreniz"
          required
          size="md"
          className="w-full"
          maxLength={30}
          mt="md"
          {...form.getInputProps("currentPwd")}
        />

        <div className="mt-md">
          <PasswordInput
            label="Yeni Şifre"
            placeholder="Yeni şifreniz"
            required
            size="md"
            className="w-full"
            maxLength={30}
            mt="md"
            {...form.getInputProps("newPwd")}
          />
          <Progress
            value={getStrength(form.values.newPwd)}
            color={
              getStrength(form.values.newPwd) < 50
                ? "red"
                : getStrength(form.values.newPwd) < 75
                ? "yellow"
                : "green"
            }
            size="sm"
            radius="xl"
            className="mt-2"
          />
          <Text size="xs" color="dimmed" mt={2}>
            Şifre en az 8 karakter, büyük harf, küçük harf ve rakam içermelidir.
          </Text>
        </div>

        <PasswordInput
          label="Yeni Şifre (Tekrar)"
          placeholder="Yeni şifreniz"
          required
          size="md"
          className="w-full"
          maxLength={30}
          mt="md"
          {...form.getInputProps("confirmPwd")}
        />

        <Group mt="xl">
          <Button
            aria-label="İptal"
            variant="default"
            onClick={onClose}
            mr="sm"
          >
            İptal
          </Button>
          <Button aria-label="Kaydet" type="submit">
            Kaydet
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
